import React from 'react';
import { Grid, FormControl, InputLabel, MenuItem, Container, Typography } from '@mui/material';
import { Select } from '@care/react-component-lib';
import ZipInput from '@/components/ZipInput';
import { Location, SxClassProps } from '@/types/common';
import { UserInfoTaxCalculator } from '@/types/seekerCC';

interface Props {
  onChangeInfo: (data: UserInfoTaxCalculator) => void;
  handleError: (e: boolean) => void;
  userInfo: UserInfoTaxCalculator;
}

const classes: SxClassProps = {
  container: (theme) => ({
    padding: theme.spacing(6, 3),
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(9, 1.5),
    },
  }),
  headingText: (theme) => ({
    margin: theme.spacing(1, 0),
    fontSize: '2.25rem',
    lineHeight: '42px',
  }),
  selectContainer: (theme) => ({
    width: '100%',
    '& .MuiInput-root': {
      backgroundColor: `${theme.palette.care?.white} !important`,
      '&:hover': {
        backgroundColor: `${theme.palette.care?.grey[100]} !important`,
      },
    },
    '& .MuiInput-input': {
      '&:focus, &:hover': {
        backgroundColor: `inherit !important`,
      },
    },
    '& .MuiFormHelperText-root': {
      marginTop: theme.spacing(0.5),
      paddingLeft: theme.spacing(0.25),
    },
  }),
};

const TaxCalculatorInputs: React.FC<Props> = ({ onChangeInfo, handleError, userInfo }) => {
  const kidsOptions = [1, 2];
  const hoursOptions = [10, 20, 40];

  const onZipInputChange = (location: Location) => {
    const newUserInfo = { ...userInfo, location };
    if (location.zipcode.length === 5) {
      onChangeInfo(newUserInfo);
    }
  };

  const inputChangeHandler = (e: { target: { name: any; value: any } }) => {
    const newUserInfo = { ...userInfo, [e.target.name]: e.target.value };
    onChangeInfo(newUserInfo);
  };

  return (
    <Container maxWidth="md" disableGutters sx={classes.container}>
      <Typography variant="h1" sx={classes.headingText}>
        Calculate your child care costs
      </Typography>
      <Grid container spacing={3} pt={2}>
        <Grid item xs={12} sm={12} md={4}>
          <FormControl variant="standard" fullWidth sx={classes.selectContainer}>
            <InputLabel id="zipcode-label">Where you live</InputLabel>
            <ZipInput
              location={userInfo.location}
              onError={handleError}
              onChange={onZipInputChange}
              showLabel={false}
              showLocationIcon={false}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={12} md={4}>
          <FormControl variant="standard" fullWidth sx={classes.selectContainer}>
            <InputLabel id="kids-option-label">Number of kids</InputLabel>
            <Select
              id="kids-option"
              name="kidsNumber"
              labelId="kinds-option-label"
              value={userInfo.kidsNumber}
              onChange={inputChangeHandler}
              MenuProps={{
                keepMounted: true,
              }}>
              {kidsOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option === 1 ? '1' : '2+'}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={12} md={4}>
          <FormControl variant="standard" fullWidth sx={classes.selectContainer}>
            <InputLabel id="hours-option-label">Hours a week you need care</InputLabel>
            <Select
              id="hours-option"
              name="hoursNumber"
              labelId="hours-option-label"
              value={userInfo.hoursNumber}
              onChange={inputChangeHandler}
              MenuProps={{
                keepMounted: true,
              }}>
              {hoursOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    </Container>
  );
};

export default TaxCalculatorInputs;
